import {
  FormView,
  getPreset,
  getUniversalAuthParams,
  hasExternalTouchpointParameters,
  Touchpoint,
} from '@account/account-portal/config'
import {
  closeAccountPortal,
  Events,
  getAccountType,
  getAuthentication,
  instanaEvent,
  updateRuntimeInfo,
  useAuthentication,
  useRuntimeInfo,
} from '@account/account-portal/lib'
import { AccountPortalProps, AccountType, LoginOption } from '@account/account-portal/types'
import { isServer, MicrofrontendProps } from '@crystal/core'
import { FC, useEffect } from 'react'

import { useViewData, ScreenViews } from '../lib'
import { trackOverlayClose, trackOverlayOpen } from '../lib/analytics'
import { Loader, useCurrentScreenView } from '../lib/current-screen-hook'

import styles from './account-portal.module.scss'
import { BannerImage, PortalLayout } from './layout'

export const AccountPortal: FC<MicrofrontendProps<AccountPortalProps>> = props => {
  const { isAuthenticated, user, authContext } = useAuthentication()
  const { isModalVisible } = useRuntimeInfo()
  const { viewData, updateViewData, showScreen } = useViewData()
  const { isInlineView, pageContext, touchpoint = Touchpoint.Unknown, screenId, preset } = viewData
  const { isAuthProvider } = getPreset(preset)
  const screenView = useCurrentScreenView(screenId)
  const { clientVersion } = props
  const prefillData = pageContext?.prefillData
  const showConsentsScreen = authContext?.shouldUpdateConsents
  const instanaMeta = {
    clientVersion,
    touchpoint,
    pageName: pageContext?.pageName,
  }
  const shouldReAuthenticate = preset === 're-authentication'
  const shouldApplyNarrowStyles = preset === 'confirmed-app' || preset === 'adidas-app' || preset === 'adidas-running'

  if (!isInlineView && props.pageContext) {
    throw new Error(`'PageContext' must not be provided in props for 'modal' presets, only in runtimeInfo.`)
  }

  useEffect(() => {
    if (isInlineView) {
      onInlineView()
    }

    return () => {
      onPortalUnmount()
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      onAuthenticated()
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (isModalVisible) {
      onModalOpen()
    }
  }, [isModalVisible])

  function onInlineView() {
    onPortalView()
  }

  function onModalOpen() {
    onPortalView()
  }

  function onPortalView() {
    setFormView()
    trackOverlayOpen()
    instanaEvent(isInlineView ? Events.PortalInlineView : Events.PortalOpened, instanaMeta)

    if (prefillData) {
      setupPrefilledView()
    } else if (showConsentsScreen) {
      showScreen(ScreenViews.MEMBERSHIP_CONSENTS)
    } else {
      showScreen(ScreenViews.LANDING_SCREEN)
    }
  }

  function onPortalUnmount() {
    trackOverlayClose()
    instanaEvent(Events.PortalClosed, instanaMeta)
  }

  function onAuthenticated() {
    const { isSessionReload } = getAuthentication()
    if (shouldReAuthenticate) {
      return
    } else {
      closeAccountPortal()
    }

    // redirect auth token to mobile app
    if (!isSessionReload && isAuthProvider) {
      redirectAuthToken()
    }
  }

  function setFormView() {
    let { formView } = getPreset(viewData.preset)
    if (hasExternalTouchpointParameters() && !isAuthProvider) {
      formView = FormView.OneStep
    }

    if (formView) {
      updateViewData({
        formView,
      })
    }
  }

  async function setupPrefilledView() {
    if (!prefillData?.email) {
      return
    }
    const { email } = prefillData
    showScreen(ScreenViews.LOADING_SCREEN)

    try {
      const { accountType, loginOptions } = await getAccountType(email.value)
      if (accountType === AccountType.Full) {
        updateViewData({
          email: email.value,
          loginMethod: loginOptions?.includes(LoginOption.Password) ? LoginOption.Password : LoginOption.Social,
        })
        showScreen(ScreenViews.LOGIN_SCREEN)
        return
      }
    } catch {
      // ignore the error
    }

    showScreen(ScreenViews.LANDING_SCREEN)
  }

  function redirectAuthToken() {
    const { redirectUri, state } = getUniversalAuthParams()
    const params = [['redirect_uri', redirectUri]]
    if (state) {
      params.push(['state', state])
    }
    const authParams = params.map(([k, v]) => `${k}=${v}`).join('&')
    location.assign(`/api/account/auth-redirect?${authParams}`)
  }

  function setInlineRuntimeInfo() {
    if (isInlineView && !isModalVisible) {
      updateRuntimeInfo({
        isAuthProvider,
        preset: props.preset,
        touchpoint: props.touchpoint,
        pageContext: props.pageContext,
        propSourceId: props.sourceId,
      })
    }
  }

  return (
    <PortalLayout
      className={`${styles.accountPortal} ${props.className || ''}`}
      isInlineView={isInlineView}
      isSSR={isServer()}>
      <div onFocus={setInlineRuntimeInfo}>
        <BannerImage presetId={viewData.preset ?? 'modal'} />
        {isAuthProvider && isAuthenticated ? (
          <Loader />
        ) : (
          <div
            data-auto-id={`account-portal-${ScreenViews[viewData.screenId]}`}
            className={shouldApplyNarrowStyles ? styles.landingScreen : ''}>
            {screenView}
          </div>
        )}
      </div>
    </PortalLayout>
  )
}
